import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, Mail, ChevronDown, Instagram, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';


const services = [
  {
    id: 'dev',
    title: 'Website Design and Development',
    link: '/web-development',
  },
  {
    id: 'revamp',
    title: 'Website Revamp',
    link: '/website-revamp',
  },
  {
    id: 'hosting',
    title: 'Server Hosting & Maintenance',
    link: '/hosting-maintenance',
  },
];


const projects = [
  {
    id: 1,
    title: "Innosin Labs Digital Transformation",
    slug: "inosin-labs", 
    client: "A trusted Singapore laboratory fabrication business with 40 years of excellence.",
    service: "Website Development & Maintenance",
    description: "Brought a local laboratory business online, creating their first digital presence. The new website showcased their expertise and services, resulting in increased customer traction and expanded market reach.",
    images: [
      "/inosin.png",
      "/inosin-2.png",
      "/inosin-3.png",
      "/inosin-4.png"
    ],
    tags: ["React", "Fast Page Speed", "Local Business"],
    color: "text-blue-600",
    delay: '100ms',
  },
  {
    id: 2,
    title: "Foundation Commercial Cleaning",
    slug: "foundation-commercial-cleaning",
    client: "A local commercial cleaning service provider delivering professional standards.",
    service: "Website Development & maintenance",
    description: "Established a robust, clean, and professional digital platform to showcase their cleaning services. The new site highlights their reliability, service standards, and facilitates easy client inquiries.",
    images: [
      "/foundationhome.png",
      "/foundation1.png",
      "/foundation3.png",
      "/foundation4.png"
    ],
    tags: ["Service Business", "Lead Generation", "Mobile Responsive"],
    color: "text-cyan-600",
    delay: '200ms',
  },
];

// --- Reusable Utility Components ---
const AnimatedSection = ({ children, animationClass = 'animate-slideUp', delay = '0ms', className = '' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? animationClass : 'opacity-0'}`}
      style={{ animationDelay: isVisible ? delay : '0ms' }}
    >
      {children}
    </div>
  );
};

// --- Image Gallery Card Component (with object-contain fix and new links) ---
const ProjectCard = ({ project }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Construct the full path for the case study
  const projectPath = `/ourworks/${project.slug}`; 

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
  };

  const accentClassName = project.color.replace('text-', '');
  const tagBgClass = `bg-${accentClassName.split('-')[0]}-100`;
  const tagTextClass = `text-${accentClassName}`;

  return (
    <AnimatedSection
      animationClass="animate-slideUp"
      delay={project.delay}
      className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 bg-white"
    >
      {/* Image Gallery Container */}
      <div className="relative h-96 overflow-hidden bg-slate-100 flex items-center justify-center">
        <img
          src={project.images[currentImageIndex]}
          alt={`${project.title} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-contain transition-opacity duration-300"
        />
        
        {/* Navigation Arrows */}
        {project.images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.preventDefault(); prevImage(); }} // Prevent link follow
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-20"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6 text-slate-900" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); nextImage(); }} // Prevent link follow
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-20"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6 text-slate-900" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {project.images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {project.images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.preventDefault(); setCurrentImageIndex(index); }} // Prevent link follow
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Project Info */}
      <div className="p-6 space-y-3">
        <p className={`text-sm font-semibold uppercase tracking-wider ${project.color}`}>
          {project.service}
        </p>
        
        {/* Title and Link to Case Study */}
        <a href={projectPath} className="group-hover:text-cyan-500 transition-colors block">
          <h3 className="text-2xl font-bold text-slate-900 leading-tight">
            {project.title}
          </h3>
        </a>

        <p className="text-sm text-slate-600 italic">
          "{project.client}"
        </p>
        <p className="text-sm text-slate-700 leading-relaxed">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 pt-2">
          {project.tags.map(tag => (
            <span
              key={tag}
              className={`px-3 py-1 ${tagBgClass} ${tagTextClass} text-xs font-semibold rounded-full`}
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="pt-4">
          <a 
            href={projectPath} 
            className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-500 hover:text-blue-600 transition-colors"
          >
            Find out more
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </AnimatedSection>
  );
};


const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                    {/* Column 1: Brand & Contact */}
                    <div className="col-span-2 md:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        
                        <div className="flex space-x-4 pt-2">
                        <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                        <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                            
                        </div>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                            
                        </ul>
                    </div>

                    {/* Column 3: Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Column 4: Get In Touch */}
                    <div className="space-y-4 col-span-2 md:col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* --- LEGAL LINKS --- */}
                <div className="flex justify-center md:justify-start space-x-6 mt-8">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>
                {/* ------------------- */}

                {/* Copyright */}
                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};
// ---------------------------------------------------------------------------------

// --- Main Component ---
export default function OurWorksPage() {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);

  
  const handleServicesClick = (e) => {
    e.stopPropagation();
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesDropdownRef]);

  
  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden">
      
      
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(5deg); } 
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-slideUp { animation: slideUp 0.6s ease-out forwards; }
        .float-animation { animation: float 6s ease-in-out infinite; }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
        }
        

        /* New Menu Modal/Overlay CSS */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            z-index: 9999; 
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open {
            opacity: 1;
            visibility: visible;
        }
        .modal-content {
            position: fixed;
            top: 0;
            right: 0;
            width: 90%;
            max-width: 400px;
            height: 100%;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto;
            padding: 3rem 1.5rem;
        }
        .modal-overlay.open .modal-content {
            transform: translateX(0);
        }
      `}</style>
      
      
      

      
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-900 font-bold cursor-pointer">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
            
        </div>
      </div>

      <main>
        <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <AnimatedSection className="text-center mb-16 space-y-4">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900">
              Our Engineered Digital Presence
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              A curated selection of the projects we've built, revamped, and launched. Navigate through the images to explore our work.
            </p>
          </AnimatedSection>

          {/* Grid updated to lg:grid-cols-1 and centered for single project */}
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 mb-16 max-w-4xl mx-auto">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>

          
          <AnimatedSection delay="200ms" className="text-center py-16 bg-white rounded-3xl shadow-2xl border border-slate-100 mx-auto max-w-5xl my-16 space-y-6">
              <h2 className="text-4xl font-extrabold text-slate-900">
                  Got a Vision? Let's Build It.
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Your next successful digital transformation starts with a free, no-obligation consultation with our engineering team.
              </p>
             
  
  <a
    href="/consult"
    className="group inline-flex items-center gap-2 px-8 py-4 
               bg-cyan-500 hover:bg-cyan-600 
               text-white font-bold 
               rounded-full 
               shadow-lg 
               transition-all duration-200 
               hover:scale-105 
               text-lg"
  >
    <span className="flex items-center gap-2">
      Book Your Free Consultation
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
    </span>
  </a>




          </AnimatedSection>

        </section>

        
        <FooterSection />
      </main>
    </div>
  );
}